import { defineCollection } from "@rebasepro/cms-types";
import { policy } from "@rebasepro/types";

/**
 * The iPhones holding each Apple Wallet card, so a change can reach them.
 *
 * Apple keeps nothing: a saved card is a file on the phone. When a member adds
 * it, the phone registers here (functions/wallet.ts, /apple/v1/devices/…) with
 * a push token; when their points or tier change, each registered phone gets
 * an empty push and downloads the card again (backend/src/apple-card.ts). One
 * row per phone and card; a phone that deletes the card unregisters, and one
 * Apple reports gone is removed on the next push.
 *
 * Not public: a push token is a way to reach somebody's phone.
 */
const walletDevicesCollection = defineCollection({
    name: "Wallet Devices",
    singularName: "Wallet Device",
    slug: "wallet_devices",
    table: "wallet_devices",
    description: "iPhones registered for updates to an Apple Wallet card.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operation: "delete", roles: ["admin"] },
        // The phones register and unregister without a signed-in user.
        { operations: ["select", "insert", "update", "delete"], condition: policy.serverContext() }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: { readOnly: true }
        },
        serial_number: {
            name: "Card",
            type: "string",
            validation: { required: true },
            description: "The card's loyalty id (LOY-…)."
        },
        device_library_id: {
            name: "Device",
            type: "string",
            validation: { required: true },
            description: "Apple's id for the phone's Wallet. Says nothing about the person."
        },
        push_token: {
            name: "Push token",
            type: "string",
            validation: { required: true },
            admin: { hideFromCollection: true }
        },
        created_at: {
            name: "Registered",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        updated_at: {
            name: "Updated At",
            type: "date",
            mode: "date_time",
            autoValue: "on_update",
            admin: { readOnly: true, hideFromCollection: true }
        }
    },
    admin: {
        icon: "Smartphone",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        listProperties: ["serial_number", "created_at"],
        openEntityMode: "side_panel",
        sort: ["created_at", "desc"],
        propertiesOrder: ["serial_number", "device_library_id", "push_token", "created_at", "updated_at"]
    }
});

export default walletDevicesCollection;
